import { usePreviewProtocol } from "hooks/preview/usePreviewProtocol";
import type { Message } from "preview-protocol";
import { useEffect, useState } from "react";
import type { OverlayState } from "types/preview";

type MessageFromPreviewToDevtool =
  | {
      type: "POSITION_UPDATE";
      state: OverlayState;
    }
  | { type: "DOCUMENT_CLICK" };

export const usePreviewOverlay = (src: string) => {
  const [state, setState] = useState<OverlayState>({ state: "NO_CORNER" });

  const previewProtocol = usePreviewProtocol<
    Message,
    MessageFromPreviewToDevtool
  >(src);

  useEffect(
    () =>
      previewProtocol.onMessage((message) => {
        switch (message.type) {
          case "POSITION_UPDATE":
            setState(message.state);
            break;

          case "DOCUMENT_CLICK":
            setState({ state: "DOCUMENT_CLICK" });
            break;
        }
      }),
    [],
  );

  useEffect(() => {
    if (previewProtocol.status === "CONNECTED") {
      previewProtocol.injectAndInvoke(function trackCorners({
        previewWindow,
        previewProtocol,
      }) {
        const cornerWidth = previewWindow.innerWidth / 3;
        const cornerHeight = previewWindow.innerHeight / 3;
        const THROTTLE_MS = 250;

        function throttle(
          callback: (event: MouseEvent) => void,
          limit: number,
        ) {
          let waiting = false;
          return function (event: MouseEvent) {
            if (!waiting) {
              callback(event);
              waiting = true;
              setTimeout(function () {
                waiting = false;
              }, limit);
            }
          };
        }

        let state: OverlayState = {
          state: "NO_CORNER",
        };

        const onMouseMove = throttle((event) => {
          const { clientX, clientY } = event;
          const prevState = state;

          // Top Left
          if (clientX < cornerWidth && clientY < cornerHeight) {
            state = {
              state: "TOP_LEFT",
            };
          }
          // Top Right
          else if (
            clientX > previewWindow.innerWidth - cornerWidth &&
            clientY < cornerHeight
          ) {
            state = {
              state: "TOP_RIGHT",
            };
          }
          // Bottom Left
          else if (
            clientX < cornerWidth &&
            clientY > previewWindow.innerHeight - cornerHeight
          ) {
            state = {
              state: "BOTTOM_LEFT",
            };
          }
          // Bottom Right
          else if (
            clientX > previewWindow.innerWidth - cornerWidth &&
            clientY > previewWindow.innerHeight - cornerHeight
          ) {
            state = {
              state: "BOTTOM_RIGHT",
            };
          } else {
            state = {
              state: "NO_CORNER",
            };
          }

          if (state.state !== prevState.state) {
            previewProtocol.sendMessage({ type: "POSITION_UPDATE", state });
          }
        }, THROTTLE_MS);

        previewWindow.addEventListener("mousemove", onMouseMove);
        document.body.addEventListener("click", () => {
          previewProtocol.sendMessage({ type: "DOCUMENT_CLICK" });
        });
      }, {});
    }
  }, [previewProtocol.status]);

  return [state, previewProtocol] as const;
};
